<template>
  <v-card>
    <v-toolbar flat dense color="primary" dark>
      <v-toolbar-title>
        <v-icon left>mdi-gas-station-outline</v-icon>
        {{ $t("Nouveau bon (admin)") }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pt-4">
      <v-form ref="form" v-model="formValid" @submit.prevent="submit">
        <v-row>
          <!-- Societe emettrice (toutes societes) -->
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.issuer_company"
              :items="allCompanies"
              item-text="name"
              item-value="pk"
              :label="$t('Société émettrice')"
              :rules="[v => !!v || $t('La société émettrice est requise')]"
              prepend-inner-icon="mdi-domain"
              outlined
              dense
              :loading="loadingCompanies"
              clearable
              @change="onIssuerChange"
            />
          </v-col>

          <!-- Societe destinataire (Business) -->
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.recipient_company"
              :items="businessCompanies"
              item-text="name"
              item-value="pk"
              :label="$t('Société destinataire (Business)')"
              :rules="[v => !!v || $t('La société destinataire est requise')]"
              prepend-inner-icon="mdi-domain"
              outlined
              dense
              :loading="loadingCompanies"
              clearable
              @change="onRecipientChange"
            />
          </v-col>

          <!-- Vehicule (du destinataire) -->
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.vehicle"
              :items="vehicles"
              :item-text="vehicleLabel"
              item-value="id"
              :label="$t('Véhicule')"
              :rules="[v => !!v || $t('Le véhicule est requis')]"
              prepend-inner-icon="mdi-car"
              outlined
              dense
              :loading="loadingVehicles"
              :disabled="!form.recipient_company"
              clearable
              :no-data-text="$t('Sélectionnez une société destinataire')"
              @change="onVehicleChange"
            />
          </v-col>

          <!-- Produit (de l'emetteur) -->
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.company_product"
              :items="products"
              item-text="name"
              item-value="id"
              :label="$t('Produit')"
              :rules="[v => !!v || $t('Le produit est requis')]"
              prepend-inner-icon="mdi-gas-station"
              outlined
              dense
              :loading="loadingProducts"
              :disabled="!form.issuer_company"
              clearable
              :no-data-text="$t('Sélectionnez une société émettrice')"
              @change="onProductChange"
            />
          </v-col>

          <!-- Station (de l'emetteur) -->
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.company_shop"
              :items="shops"
              item-text="name"
              item-value="id"
              :label="$t('Station')"
              prepend-inner-icon="mdi-store-outline"
              outlined
              dense
              :loading="loadingShops"
              :disabled="!form.issuer_company"
              clearable
              :no-data-text="$t('Sélectionnez une société émettrice')"
            />
          </v-col>

          <!-- Mode (montant ou litres) -->
          <v-col cols="12" md="4">
            <v-select
              v-model="form.is_all_money"
              :items="modeOptions"
              :label="$t('Mode')"
              outlined
              dense
              prepend-inner-icon="mdi-format-list-bulleted"
            />
          </v-col>

          <!-- Litres -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.purchase_liters"
              :label="$t('Litres')"
              type="number"
              step="0.01"
              min="0"
              outlined
              dense
              prepend-inner-icon="mdi-numeric"
              :disabled="form.is_all_money === true"
              :rules="form.is_all_money ? [] : [v => (v && v > 0) || $t('Litres requis')]"
              :hint="productPrice ? $t('Prix unitaire : ') + formatNumber(productPrice) + ' XOF/L' : ''"
              persistent-hint
              @input="onLitersInput"
            />
          </v-col>

          <!-- Montant -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.purchase_amount"
              :label="$t('Montant (XOF)')"
              type="number"
              step="1"
              min="0"
              outlined
              dense
              prepend-inner-icon="mdi-cash"
              :rules="[v => (v && v > 0) || $t('Montant requis')]"
              @input="onAmountInput"
            />
          </v-col>

          <!-- Date validite -->
          <v-col cols="12" md="6">
            <v-menu
              v-model="validUntilMenu"
              :close-on-content-click="false"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.valid_until"
                  :label="$t('Date de validité')"
                  :rules="[v => !!v || $t('Date de validité requise')]"
                  prepend-inner-icon="mdi-calendar-end"
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="form.valid_until"
                :min="todayStr"
                @input="validUntilMenu = false"
              />
            </v-menu>
          </v-col>

          <!-- Frequency -->
          <v-col cols="12" md="6">
            <v-select
              v-model="form.frequency"
              :items="frequencyOptions"
              :label="$t('Fréquence')"
              outlined
              dense
              prepend-inner-icon="mdi-repeat"
            />
          </v-col>

          <!-- BL -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.bl_number"
              :label="$t('N° BL')"
              outlined
              dense
              prepend-inner-icon="mdi-barcode"
            />
          </v-col>

          <!-- Destination -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.destination"
              :label="$t('Destination')"
              outlined
              dense
              prepend-inner-icon="mdi-map-marker"
            />
          </v-col>

          <!-- Options -->
          <v-col cols="12">
            <div class="d-flex flex-wrap" style="gap: 16px;">
              <v-switch
                v-model="form.allow_successive"
                :label="$t('Successifs autorisés')"
                hide-details
                dense
                inset
              />
              <v-switch
                v-model="form.accumulate_remaining"
                :label="$t('Cumul du restant')"
                hide-details
                dense
                inset
              />
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions class="pa-4">
      <v-spacer />
      <v-btn text @click="$emit('close')">{{ $t("Annuler") }}</v-btn>
      <v-btn
        color="primary"
        depressed
        :loading="submitting"
        :disabled="!formValid"
        @click="submit"
      >
        <v-icon left>mdi-check</v-icon>
        {{ $t("Créer le bon") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import CompanyService from "@/services/CompanyService"

export default {
  name: "AdminVoucherEditor",

  data() {
    return {
      formValid: false,
      submitting: false,

      loadingCompanies: false,
      loadingVehicles: false,
      loadingProducts: false,
      loadingShops: false,

      businessCompanies: [],
      allCompanies: [],
      vehicles: [],
      products: [],
      shops: [],

      validUntilMenu: false,
      todayStr: new Date().toISOString().slice(0, 10),

      form: {
        issuer_company: null,
        recipient_company: null,
        vehicle: null,
        company_product: null,
        company_shop: null,
        purchase_liters: null,
        purchase_amount: null,
        valid_until: this.tomorrowStr(),
        frequency: "Monthly",
        is_all_money: false,
        bl_number: "",
        destination: "",
        allow_successive: false,
        accumulate_remaining: false,
      },

      modeOptions: [
        { text: this.$t("Litres + Montant"), value: false },
        { text: this.$t("Montant uniquement"), value: true },
      ],
      frequencyOptions: [
        { text: "Mensuel", value: "Monthly" },
        { text: "Hebdomadaire", value: "Weekly" },
        { text: "Quotidien", value: "Daily" },
        { text: "Trimestriel", value: "Quarterly" },
        { text: "Semestriel", value: "Semi-annual" },
        { text: "Annuel", value: "Annual" },
        { text: "Unique", value: "Single" },
      ],
    }
  },

  computed: {
    productPrice() {
      const id = this.form.company_product
      if (!id) return 0
      const p = this.products.find(x => x.id === id)
      return p ? Number(p.price || 0) : 0
    },
  },

  created() {
    this.fetchAllCompanies()
  },

  methods: {
    tomorrowStr() {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      return `${y}-${m}-${dd}`
    },

    formatNumber(v) {
      return Number(v || 0).toLocaleString("fr-FR")
    },

    vehicleLabel(v) {
      const driver = `${v.driver_first_name || ""} ${v.driver_last_name || ""}`.trim()
      return driver
        ? `${v.registration_number} — ${driver}`
        : v.registration_number || `Véhicule #${v.id}`
    },

    fetchAllCompanies() {
      this.loadingCompanies = true
      CompanyService.getAllCompanies()
        .then(r => {
          const list = Array.isArray(r) ? r : (r?.results || [])
          this.allCompanies = list
          this.businessCompanies = list.filter(c => c.company_type === "Business")
        })
        .catch(() => {
          this.allCompanies = []
          this.businessCompanies = []
        })
        .finally(() => { this.loadingCompanies = false })
    },

    onIssuerChange(companyId) {
      // Reset dependants lies a l'emetteur
      this.form.company_product = null
      this.form.company_shop = null
      this.products = []
      this.shops = []
      if (!companyId) return
      this.fetchProducts(companyId)
      this.fetchShops(companyId)
    },

    onRecipientChange(companyId) {
      // Reset dependants lies au destinataire
      this.form.vehicle = null
      this.vehicles = []
      if (!companyId) return
      this.fetchVehicles(companyId)
    },

    onVehicleChange(vehicleId) {
      if (!vehicleId) return
      const v = this.vehicles.find(x => x.id === vehicleId)
      if (!v) return
      // Si le vehicule a un produit rattache, on l'utilise comme suggestion
      // (uniquement si le produit existe parmi les produits charges de l'emetteur).
      const productId = v.product?.id || v.product || v.product_id
      if (!productId) return
      const matching = this.products.find(p => p.id === productId)
      if (matching) {
        this.form.company_product = matching.id
        // Recalcul auto : si l'utilisateur avait deja saisi des litres, on
        // met a jour le montant. Sinon si montant deja saisi, on calcule litres.
        if (this.form.purchase_liters > 0) {
          this.recomputeFromLiters()
        } else if (this.form.purchase_amount > 0) {
          this.recomputeFromAmount()
        }
      }
    },

    onProductChange() {
      // Si on change de produit, on recalcule le montant (si litres saisis)
      // ou les litres (si montant saisi).
      if (this.form.is_all_money) return
      if (this.form.purchase_liters > 0) this.recomputeFromLiters()
      else if (this.form.purchase_amount > 0) this.recomputeFromAmount()
    },

    onLitersInput() {
      if (this.form.is_all_money) return
      this.recomputeFromLiters()
    },

    onAmountInput() {
      if (this.form.is_all_money) return
      this.recomputeFromAmount()
    },

    recomputeFromLiters() {
      const price = this.productPrice
      const liters = Number(this.form.purchase_liters || 0)
      if (price > 0 && liters > 0) {
        this.form.purchase_amount = Math.round(liters * price)
      }
    },

    recomputeFromAmount() {
      const price = this.productPrice
      const amount = Number(this.form.purchase_amount || 0)
      if (price > 0 && amount > 0) {
        // Arrondi a 2 decimales
        this.form.purchase_liters = Math.round((amount / price) * 100) / 100
      }
    },

    fetchVehicles(companyId) {
      this.loadingVehicles = true
      AdminServices.getCompanyVehicles(this, companyId)
        .then(r => {
          this.vehicles = Array.isArray(r) ? r : (r?.results || [])
        })
        .catch(() => { this.vehicles = [] })
        .finally(() => { this.loadingVehicles = false })
    },

    fetchProducts(companyId) {
      this.loadingProducts = true
      AdminServices.getCompanyProducts(this, companyId)
        .then(r => {
          this.products = Array.isArray(r) ? r : (r?.results || [])
        })
        .catch(() => { this.products = [] })
        .finally(() => { this.loadingProducts = false })
    },

    fetchShops(companyId) {
      this.loadingShops = true
      AdminServices.getCompanyShops(this, companyId)
        .then(r => {
          this.shops = Array.isArray(r) ? r : (r?.results || [])
        })
        .catch(() => { this.shops = [] })
        .finally(() => { this.loadingShops = false })
    },

    submit() {
      if (!this.$refs.form.validate()) return
      this.submitting = true
      const payload = { ...this.form }
      // issuer_company n'est pas un champ du modele PurchaseVoucher : il sert
      // uniquement a filtrer produits/stations cote UI. On l'envoie quand meme
      // pour que le backend puisse le valider et l'utiliser comme contexte.
      // Sanitize : si is_all_money, on n'envoie pas de litres
      if (payload.is_all_money) payload.purchase_liters = 0
      // Le backend attend un datetime ISO, pas un YYYY-MM-DD nu.
      // On envoie a 23:59:59 local pour couvrir toute la journee.
      if (payload.valid_until && payload.valid_until.length === 10) {
        const d = new Date(payload.valid_until + "T23:59:59")
        payload.valid_until = d.toISOString()
      }
      AdminServices.createVoucher(this, payload)
        .then(() => {
          this.$store.commit("app/message", {
            msg: this.$t("Bon créé avec succès"), color: "success",
          })
          this.$emit("saved")
        })
        .catch(err => {
          const data = err?.response?.data || {}
          const msg = data.detail
            || data.recipient_company
            || data.vehicle
            || data.company_product
            || data.purchase_amount
            || this.$t("Échec de la création du bon")
          this.$store.commit("app/message", {
            msg: typeof msg === "string" ? msg : JSON.stringify(msg),
            color: "error",
          })
        })
        .finally(() => { this.submitting = false })
    },
  },
}
</script>
